/**
 * Product-detail bulk tools for Products & COGS → Actions (Rishabh, ops-iq 2026-10-23:
 * "it should be done through Products module", and for any product field, not only Manufacturer).
 *
 *   - Fill manufacturers from POs: preview (SKU → maker from purchase orders) → tick → confirm.
 *     Only blank Manufacturers are filled; rows with several makers are listed, never filled.
 *   - Export products / Update products from CSV: the same product_master endpoints the
 *     Inventory Master report uses (export.csv round-trips into bulk-update by id).
 *
 * Returns { actions, dialogs } so ProductCOGS only adds the menu entries and renders the dialogs.
 */
import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import {
  FileDownload as ExportIcon,
  EditNote as BulkEditIcon,
  Factory as FactoryIcon,
} from '@mui/icons-material';
import BulkImportDialog from '../common/BulkImportDialog';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

// Mirrors PM_IMPORT_COLUMNS in reports/InventoryMasterReport.js (same backend PM_WRITABLE_* lists).
// Each column also accepts its on-screen label as a header ("Primary SKU", "Manufacturer"), so a
// hand-made sheet works as well as an exported one. In update mode nothing but `id` is required:
// blank cells are skipped, so a file can carry just id + the columns being changed.
const COLUMNS = [
  ['primary_sku', 'Primary SKU', { aliases: ['sku', 'code'] }],
  ['product_name', 'Product name', { aliases: ['name', 'title'] }],
  ['primary_gtin', 'Primary GTIN', { aliases: ['gtin', 'ean', 'upc'] }],
  ['product_type', 'Product type', { aliases: ['type'] }],
  ['category', 'Category'],
  ['sub_category', 'Sub-category', { aliases: ['subcategory'] }],
  ['product_level', 'Product level', { enum: ['L0', 'L1', 'L2'] }],
  ['is_bundle', 'Is bundle?', { type: 'boolean' }],
  ['is_prepacked', 'Is prepacked?', { type: 'boolean' }],
  ['is_active', 'Active?', { type: 'boolean' }],
  ['short_description', 'Short description', { aliases: ['short_desc'] }],
  ['long_description', 'Long description', { aliases: ['description', 'desc'] }],
  ['image_url', 'Image URL', { aliases: ['image'] }],
  ['thumbnail_url', 'Thumbnail URL'],
  ['hs_code', 'HS code', { aliases: ['hts_code', 'tariff_code'] }],
  ['country_of_origin', 'Country of origin', { aliases: ['origin', 'coo'] }],
  ['customs_description', 'Customs description'],
  ['customs_value', 'Customs value', { type: 'number' }],
  ['weight_kg', 'Weight (kg)', { type: 'number', aliases: ['weight'] }],
  ['weight_unit', 'Weight unit'],
  ['length_cm', 'Length (cm)', { type: 'number' }],
  ['width_cm', 'Width (cm)', { type: 'number' }],
  ['height_cm', 'Height (cm)', { type: 'number' }],
  ['dimension_unit', 'Dimension unit'],
  ['volume_ml', 'Volume (ml)', { type: 'number' }],
  ['storage_conditions', 'Storage conditions'],
  ['is_hazmat', 'Hazmat?', { type: 'boolean' }],
  ['hazmat_class', 'Hazmat class'],
  ['is_fragile', 'Fragile?', { type: 'boolean' }],
  ['shelf_life_days', 'Shelf life (days)', { type: 'number' }],
  ['requires_refrigeration', 'Needs refrigeration?', { type: 'boolean' }],
  ['vendor', 'Vendor'],
  ['manufacturer', 'Manufacturer'],
  ['manufacturer_part_number', 'MPN', { aliases: ['part_number'] }],
  ['lead_time_days', 'Lead time (days)', { type: 'number' }],
  ['moq', 'MOQ', { type: 'number' }],
  ['case_pack', 'Case pack', { type: 'number' }],
  ['msrp', 'MSRP', { type: 'number' }],
  ['msrp_currency', 'MSRP currency', { aliases: ['currency'] }],
  ['tags', 'Tags'],
  ['notes', 'Notes'],
];
const UPDATE_COLUMNS = COLUMNS.map(([field, label, extra = {}]) => ({
  field, label, ...extra, aliases: [...(extra.aliases || []), label],
}));
// New products (insert): SKU + name required, as the bulk-import endpoint checks.
const IMPORT_COLUMNS = UPDATE_COLUMNS.map((c) => (
  ['primary_sku', 'product_name', 'product_level'].includes(c.field) ? { ...c, required: true } : c
));
const IMPORT_SAMPLE = [
  {
    primary_sku: 'SS-NEW-SKU-1', product_name: 'Example finished good 50ml', product_level: 'L1',
    category: 'Skincare', is_active: 1, hs_code: '3304.99.00', country_of_origin: 'AU',
    weight_kg: 0.065, manufacturer: 'Factory name', moq: 1000, lead_time_days: 60,
  },
  {
    primary_sku: 'SS-NEW-PART-1', product_name: 'Example part - empty jar 50ml', product_level: 'L0',
    is_active: 1, vendor: 'Supplier name', moq: 5000,
  },
];

export default function useProductMasterBulkTools({ selectedBrand, onChanged, setSuccess, setError }) {
  const brandId = selectedBrand?.id;
  const [updateOpen, setUpdateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [fillOpen, setFillOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [fillError, setFillError] = useState(null);
  // 'manufacturer' = L1 finished goods -> Manufacturer; 'supplier' = L0 parts -> Vendor (Supplier).
  const [fillKind, setFillKind] = useState('manufacturer');
  const isSupplier = fillKind === 'supplier';
  const fillPath = isSupplier ? 'supplier-fill' : 'manufacturer-fill';
  const previewSeq = useRef(0); // a late preview from a closed dialog must not land in the next one

  const handleExport = async () => {
    if (!brandId) return;
    try {
      const res = await axios.get(`/api/brands/${brandId}/products/master/export.csv`, { responseType: 'blob' });
      let filename = `products-brand-${brandId}-${new Date().toISOString().slice(0, 10)}.csv`;
      const m = (res.headers?.['content-disposition'] || '').match(/filename="?([^"]+)"?/);
      if (m) filename = m[1];
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSuccess?.(`Exported ${filename}`);
    } catch (e) {
      setError?.(apiErrorMessage(e, 'Export failed'));
    }
  };

  const handleImportRows = async (rows) => {
    if (!brandId) throw new Error('Select a brand first');
    const res = await axios.post(`/api/brands/${brandId}/products/master/bulk-import`, { rows });
    onChanged?.();
    return res.data;
  };

  const handleUpdateRows = async (rows) => {
    if (!brandId) throw new Error('Select a brand first');
    const updates = rows.map((r) => ({ ...r, id: parseInt(r.id, 10) }));
    const res = await axios.post(`/api/brands/${brandId}/products/master/bulk-update`, { updates });
    onChanged?.();
    return res.data;
  };

  const openFill = async (kind) => {
    if (!brandId) return;
    const seq = ++previewSeq.current;
    setFillKind(kind);
    setFillOpen(true);
    setPlan(null);
    setChecked(new Set());
    setFillError(null);
    setLoading(true);
    try {
      const res = await axios.get(`/api/brands/${brandId}/products/master/${kind === 'supplier' ? 'supplier-fill' : 'manufacturer-fill'}`);
      if (seq !== previewSeq.current) return;
      setPlan(res.data);
      setChecked(new Set((res.data.fill || []).map((f) => f.id)));
    } catch (e) {
      if (seq !== previewSeq.current) return;
      setFillError(apiErrorMessage(e, 'Could not load the preview'));
    } finally {
      if (seq === previewSeq.current) setLoading(false);
    }
  };

  const applyFill = async () => {
    setApplying(true);
    setFillError(null);
    try {
      const res = await axios.post(`/api/brands/${brandId}/products/master/${fillPath}`, { ids: Array.from(checked) });
      const { filled = 0, skipped = 0, mismatched = 0 } = res.data || {};
      setFillOpen(false);
      setSuccess?.(`${isSupplier ? 'Supplier' : 'Manufacturer'} filled on ${filled} ${isSupplier ? 'part' : 'product'}${filled === 1 ? '' : 's'}`
        + (skipped ? ` · ${skipped} skipped (already set meanwhile)` : '')
        + (mismatched ? ` · ${mismatched} could not be confirmed, please refresh and check` : ''));
      onChanged?.();
    } catch (e) {
      setFillError(apiErrorMessage(e, 'Fill failed'));
    } finally {
      setApplying(false);
    }
  };

  const toggle = (id) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const actions = useMemo(() => [
    {
      key: 'fill-manufacturers',
      label: 'Fill manufacturers from POs',
      hint: 'Preview each L1 product\'s maker from its purchase orders, then fill the blank ones.',
      icon: <FactoryIcon fontSize="small" />,
      group: 'Products',
      onClick: () => openFill('manufacturer'),
    },
    {
      key: 'fill-part-suppliers',
      label: 'Fill part suppliers from POs',
      hint: 'Preview each part\'s supplier from its latest outside PO, then fill the blank ones.',
      icon: <FactoryIcon fontSize="small" />,
      group: 'Products',
      onClick: () => openFill('supplier'),
    },
    {
      key: 'import-products',
      label: 'Import new products',
      hint: 'Add products that are not in SCM yet: download the template, fill it, upload. SKU, name and level required.',
      icon: <BulkEditIcon fontSize="small" />,
      group: 'Products',
      onClick: () => setImportOpen(true),
    },
    {
      key: 'export-products',
      label: 'Export products',
      hint: `Downloads ${selectedBrand?.name || 'brand'}'s product details as CSV (edit, then update from CSV)`,
      icon: <ExportIcon fontSize="small" />,
      group: 'Products',
      onClick: handleExport,
    },
    {
      key: 'update-products',
      label: 'Update products from CSV',
      hint: 'Export → keep id + only the columns you change → upload. Matches by id; blank cells are left unchanged.',
      icon: <BulkEditIcon fontSize="small" />,
      group: 'Products',
      onClick: () => setUpdateOpen(true),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [brandId, selectedBrand?.name]);

  const fill = plan?.fill || [];
  const decide = plan?.decide || [];

  const dialogs = (
    <>
      <BulkImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        entityLabel="product"
        mode="insert"
        columnSpec={IMPORT_COLUMNS}
        sampleRows={IMPORT_SAMPLE}
        onSubmit={handleImportRows}
        helpText={`Add new ${selectedBrand?.name || 'brand'} products: download the CSV or Excel template, one row per product (primary_sku, product_name and product_level required; L1 = finished good, L0 = part). Existing SKUs are refused, so nothing already in SCM is changed.`}
      />
      <BulkImportDialog
        open={updateOpen}
        onClose={() => setUpdateOpen(false)}
        entityLabel="product"
        mode="update"
        columnSpec={UPDATE_COLUMNS}
        onSubmit={handleUpdateRows}
        helpText={`Update ${selectedBrand?.name || 'brand'} products: start from "Export products", keep the id column and ONLY the columns you are changing (delete the others — Excel can alter codes like GTINs). Blank cells are left unchanged; __CLEAR__ empties a field.`}
      />
      <Dialog open={fillOpen} onClose={() => !applying && setFillOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isSupplier ? 'Fill part suppliers' : 'Fill manufacturers'} from POs — {selectedBrand?.name}</DialogTitle>
        <DialogContent dividers>
          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
          {fillError && <Alert severity="error" sx={{ mb: 2 }}>{fillError}</Alert>}
          {plan && (
            <>
              {isSupplier ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Supplier = the outside supplier on each part's latest purchase order for this brand. Our own
                companies and "Factory (Untracked)" never count. This saves the supplier onto the part itself
                (the Supplier column may already show a PO supplier, so the screen can look the same). Only
                blank Suppliers are filled; anything already typed stays as it is. {plan.already_set} already
                set. "Also bought from" lists other suppliers — untick a row to set it yourself.
              </Typography>
              ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Maker = the outside supplier on each product's purchase orders (or on its MANUFACTURE /
                bulk-fill part). Our own companies and "Factory (Untracked)" never count. Only blank
                Manufacturers are filled; anything already typed stays as it is.
                {' '}{plan.already_set} already set. "Found on" shows where the maker comes from — untick a
                purchase-PO supplier that only traded the product.
              </Typography>
              )}
              {fill.length === 0 ? (
                <Alert severity="info">{isSupplier ? 'Nothing to fill: no part with a blank Supplier has an outside supplier on its POs.' : 'Nothing to fill: no blank Manufacturer has a single clear maker on its POs.'}</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={checked.size === fill.length}
                          indeterminate={checked.size > 0 && checked.size < fill.length}
                          onChange={(e) => setChecked(e.target.checked ? new Set(fill.map((f) => f.id)) : new Set())}
                        />
                      </TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>{isSupplier ? 'Supplier to fill' : 'Manufacturer to fill'}</TableCell>
                      <TableCell>{isSupplier ? 'Also bought from' : 'Found on'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fill.map((f) => (
                      <TableRow key={f.id} hover onClick={() => toggle(f.id)} sx={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox"><Checkbox checked={checked.has(f.id)} /></TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{f.sku}</TableCell>
                        <TableCell>{f.name}</TableCell>
                        <TableCell>{isSupplier ? f.supplier : f.manufacturer}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', whiteSpace: isSupplier ? 'normal' : 'nowrap' }}>
                          {isSupplier ? (f.others || []).join(', ') : f.via}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {decide.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <strong>Needs your decision ({decide.length})</strong> — more than one maker on the POs, so these are not filled.
                  Set them with the ✏️ edit on the product row:
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {decide.map((d) => <li key={d.id}>{d.sku} — {d.makers.join(' / ')}</li>)}
                  </Box>
                </Alert>
              )}
              {(plan.no_po_items || []).length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>No supplier PO in SCM ({plan.no_po_items.length})</strong> — only our own companies or
                  "Factory (Untracked)" on their POs, or none at all, so these stay blank. Set them with the ✏️ edit:
                  <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: 180, overflowY: 'auto' }}>
                    {plan.no_po_items.map((d) => <li key={d.id}>{d.sku} — {d.name}</li>)}
                  </Box>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFillOpen(false)} disabled={applying}>Cancel</Button>
          <Button variant="contained" onClick={applyFill} disabled={applying || loading || !plan || checked.size === 0}>
            {applying ? 'Filling…' : `Fill ${checked.size} ${isSupplier ? 'part' : 'product'}${checked.size === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return { actions, dialogs };
}
